'use client'

import { MAX_FEATURED_POSTS, MAX_POSTS_PER_CATEGORY, MAX_POSTS_PER_PAGE } from '@/lib/blog/post-services'
import { fetchJson } from '@/lib/utils/fetch-json'
import { cn } from '@/lib/utils/utils'
import type { Category, Post, PostsApiResponse } from '@/types/blog'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { FaCheck, FaMagnifyingGlass, FaXmark } from 'react-icons/fa6'
import Carousel from '../carousel'
import { getI18n, getLocalizedUri, I18nDictionary, LangCode } from '../i18n/lang-context'
import MD from '../markdown'
import { FlexCol } from '../ui/flex'
import { SCREEN_WIDTHS, useScreen } from '../utils/windowSize'
import PostCard from './post-card'
import PostCardFeatured from './post-card-featured'
import { useInfiniteScroll } from './useInfiniteScroll'

const I18N: I18nDictionary = {
  title: 'Blog',
  baseline: {
    en: 'Let’s *share* what we *learn*',
    fr: '*Partageons* nos *connaissances*'
  },
  featuredPostsTitle: { en: 'Featured Posts', fr: 'Billets Populaires' },
  newPostsTitle: { en: 'Last Posts', fr: 'Derniers Billets' },
  categories: { en: 'Categories', fr: 'Catégories' },
  search: { en: 'Search', fr: 'Rechercher' },
  searchPlaceholder: { en: 'Search posts', fr: 'Rechercher des billets' },
  loading: { en: 'Loading...', fr: 'Chargement...' },
  noResults: { en: 'No posts found', fr: 'Aucun billet trouvé' },
  noResultsDescription: {
    en: 'Try adjusting your search terms or filters',
    fr: 'Essayez d\'ajuster vos termes de recherche ou vos filtres'
  },
  loadMore: { en: 'Load more posts', fr: 'Charger plus d\'articles' },
  loadPrevious: { en: 'Load previous posts', fr: 'Charger les articles précédents' }
}

/**
 * Post Component
 * ---------------------------------------------------------------------------------------------------------------------
 */
export default function PostCards({ initialPosts, hasNextInit, totalCount, categories, langCode, className = '', initialPage = 1 }: 
  { initialPosts: Post[], hasNextInit: boolean, totalCount: number, categories: Category[], langCode: LangCode, className?: string, initialPage?: number }) {

  const i18n = getI18n(I18N, langCode)
  const screen = useScreen()
  const pathname = usePathname()

  const [searchString, setSearchString] = useState<string>('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const refInputSearch = useRef<HTMLInputElement | null>(null)

  const [posts, setPosts] = useState<Post[]>(initialPosts)
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>(initialPosts.filter(post => post.featuredPost))

  // Bidirectional Pagination States
  const [nextPage, setNextPage] = useState(initialPage + 1)
  const [prevPage, setPrevPage] = useState(initialPage - 1)
  const [hasNext, setHasNext] = useState(hasNextInit)
  const [hasPrev, setHasPrev] = useState(initialPage > 1)
  const [loadingNext, setLoadingNext] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)

  const [isMounted, setIsMounted] = useState(false)

  const [totalPostsCount] = useState(totalCount)
  const [filteredPostsCount, setFilteredPostsCount] = useState(totalCount)
  
  const loaderRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  const lastFetchParams = useRef({ search: searchString, cats: selectedCategories })

  useEffect(() => setIsMounted(true), [])

  const reloadPosts = async () => {
    /* Posts are currently being loaded */
    if (isLoading()) return
    setLoadingMore(true, true)

    const TARGET_PAGE = 1
    try {
      const searchParams = new URLSearchParams({
        page: TARGET_PAGE.toString(),
        first: MAX_POSTS_PER_PAGE.toString(),
        lang: langCode
      })
      if (searchString) searchParams.set('search', searchString)
      if (selectedCategories.length > 0) searchParams.set('categories', selectedCategories.join(','))

      const res = await fetchJson<PostsApiResponse>(`/api/posts?${searchParams}`)
      
      setPosts(res.edges.map(e => e.node))
      
      setNextPage(TARGET_PAGE + 1)
      setPrevPage(TARGET_PAGE - 1)
      setHasNext(res.hasNext)
      setHasPrev(TARGET_PAGE > 1)
      setFilteredPostsCount(res.totalCount)
    } catch (error) {
      console.error('Error reloading posts:', error)
    } finally {
      setLoadingMore(true, false)
    }
  }

  const loadFeaturedPosts = async () => {
    try {
      const searchParams = new URLSearchParams({
        page: '1',
        first: MAX_FEATURED_POSTS.toString(),
        lang: langCode,
        featured: 'true',
        orderBy: 'publishedAt_DESC'
      })

      const res = await fetchJson<PostsApiResponse>(`/api/posts?${searchParams}`)
      const allFeaturedPosts = res.edges.map(e => e.node).filter(post => post.featuredPost)
      setFeaturedPosts(allFeaturedPosts)
    } catch (error) {
      console.error('Error loading featured posts:', error)
      setFeaturedPosts(initialPosts.filter(post => post.featuredPost))
    }
  }

  const isLoading = () => {
    return isLoadingMore(true) || isLoadingMore(false)
  }
  const isLoadingMore = (isNext: boolean) => {
    return isNext && loadingNext || !isNext && loadingPrev
  }
  const setLoadingMore = (isNext: boolean, loading: boolean) => {
    isNext ? setLoadingNext(loading) : setLoadingPrev(loading)
  }
  const hasMore = (isNext: boolean) => {
    return isNext ? hasNext : hasPrev
  }
  const getMorePage = (isNext: boolean) => {
    return isNext ? nextPage : prevPage
  }

  const loadMore = async (isNext: boolean) => {
    /* No more post or are currently being loaded */
    if (!hasMore(isNext) || isLoading()) return
    
    setLoadingMore(isNext, true)
    let morePage = getMorePage(isNext)
    try {
      const searchParams = new URLSearchParams({
        page: morePage.toString(),
        first: MAX_POSTS_PER_PAGE.toString(), 
        lang: langCode
      })
      if (searchString) searchParams.set('search', searchString)
      if (selectedCategories.length > 0) searchParams.set('categories', selectedCategories.join(','))

      const res = await fetchJson<PostsApiResponse>(`/api/posts?${searchParams}`)
      const newNodes = res.edges.map(e => e.node)
      
      setPosts(existing => {
        const existingIds = new Set(existing.map(e => e.id))
        const uniqueNewNodes = newNodes.filter(n => !existingIds.has(n.id))
        return isNext ? [...existing, ...uniqueNewNodes] : [...uniqueNewNodes, ...existing]
      })
      
      morePage = isNext ? morePage + 1 : morePage - 1
      isNext ? setNextPage(morePage) : setPrevPage(morePage)
      isNext ? setHasNext(res.hasNext) : setHasPrev(morePage > 0)
    } catch (error) {
      console.error('Error loading', isNext ? 'next' : 'prev', ' posts:', error)
    } finally {
      setLoadingMore(isNext, false)
    }
  }

  useEffect(() => {
    if (screen.lg && refInputSearch.current) {
      refInputSearch.current.focus({ preventScroll: true })
    }
  }, [screen.lg])

  useEffect(() => {
    loadFeaturedPosts()
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (lastFetchParams.current.search === searchString &&
        lastFetchParams.current.cats === selectedCategories) {
        return
    }

    const timeoutId = setTimeout(() => {
      lastFetchParams.current = { search: searchString, cats: selectedCategories }
      reloadPosts()
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchString, selectedCategories])

  const switchCategory = (catId: string) => {
    setSelectedCategories((prevSelectedCategories: string[]) => {
      const newSelectedCategories = prevSelectedCategories.slice()
      const catIndex = prevSelectedCategories.indexOf(catId)
      if (catIndex >= 0) {
        newSelectedCategories.splice(catIndex, 1)
      } else {
        newSelectedCategories.push(catId)
      }
      return newSelectedCategories
    })
  }

  const loadMoreButton = (isNext: boolean) => {
    /* No button to display if not more post to be loaded */
    if(!hasMore(isNext)) return <></>
    const morePage = getMorePage(isNext)
    const url = getLocalizedUri('/blog' + (!isNext && morePage < 2 ? '' : `?page=${morePage}`), langCode)
    return <Link 
      href={url}
      prefetch={false}
      onClick={isNext ? () => loadMore(true) : () => loadMore(false)} 
      className={cn(
        "rounded-full px-8 py-3 transition-all font-medium border border-primary text-primary hover:bg-primary hover:text-white",
        isLoadingMore(isNext) ?
          "opacity-50 pointer-events-none" : // Always show "loading" while in process
          !isNext ?
            "opacity-100" : // Prev action button always shown because it means that pagination was accessed directly using "?page=" in url
            isMounted ? "opacity-0 pointer-events-none h-0 overflow-hidden" : "opacity-100" // Next action button only shown when isMounted
            // is false, which means that javascript is not enabled. So no infinite scroll is in action (otherwise, hidden and not actionable)
      )}
    >
      {isLoadingMore(isNext) ? i18n.loading : isNext ? i18n.loadMore : i18n.loadPrevious}
    </Link>
  }

  useInfiniteScroll(loaderRef, () => loadMore(true), hasNext && isMounted, nextPage)

  // Rendering
  //--------------------------------------------------------------------------------------------------------------------
  return (
    <FlexCol className={cn(className, 'items-center gap-4')}>

      <section id='SECTION_HEAD' className={'relative flex h-fit w-full flex-col items-center justify-center overflow-hidden pt-[50px]'}>

        <div id='HEAD_PANEL'
          className={'max-w-[100vw] sm:max-w-[80vw] flex flex-col w-content justify-start items-end p-4 sm:p-8 sm:py-4 my-4 ' +
            'rounded-md text-main-lightest overflow-hidden'}>

          <div className={'w-full whitespace-nowrap text-right font-[200] leading-[50px] sm:leading-[65px] md:leading-[70px] lg:leading-[100px]'}>
            <h1 className={'text-[2.8rem] font-[300] sm:text-[3.5rem] md:text-[4rem] lg:text-[4.5rem]'}><span className='text-primary dark:text-primaryBright'><span className='text-xs font-extralight opacity-30'>{langCode.toLocaleUpperCase()}</span>{'//'}</span> {i18n.title}</h1>
          </div>

          <div className={'mt-4 w-full whitespace-nowrap text-right'}>
            <h2><MD className={'text-[1rem] font-[500] sm:text-[1.2rem] md:text-[1.4rem] lg:text-[1.5rem]'} langCode={langCode}>{i18n.baseline}</MD></h2>
          </div>

        </div>

      </section>

      <div id='HEADER'
        className={'flex flex-col items-start justify-between py-4 lg:flex-row'}
        style={{
          width: screen.lg ? SCREEN_WIDTHS['lg'] : '100%',
          maxWidth: screen.lg ? SCREEN_WIDTHS['lg'] : '100%'
        }}>

        <div id='FEATURED_POSTS'
          className={'flex w-full flex-col lg:w-fit'}>

          <div id='FEATURED_POSTS_TITLE'
            className={'text-main-dark/90 drop-shadow-light1px mx-4 my-0 text-[24px] sm:text-[32px] lg:m-4'}>
            {i18n.featuredPostsTitle}
          </div>

          <Carousel
            fullWidth={!screen.lg}
            width={658}
            height={screen.sm ? (SCREEN_WIDTHS['md'] / 2) : screen.w}>
            {featuredPosts
              .map((post: Post, index: number) => (<PostCardFeatured post={post} key={'post-featured-' + index} langCode={langCode} />))}
          </Carousel>

        </div>

        <div id='FILTERS_WRAPPER'
          className={'flex w-full flex-col items-stretch lg:w-fit'}>

          <div id='SEARCH_WRAPPER'
            className={'flex w-full flex-col lg:w-auto'}>

            <div id='SEARCH_TITLE'
              className={'text-main-dark/90 drop-shadow-light1px mx-4 my-0 flex items-center justify-between text-[24px] sm:text-[32px] lg:m-4'}>
              {i18n.search}
              <div className={cn('flex text-[24px]', filteredPostsCount === totalPostsCount ? 'opacity-30' : 'opacity-100')}>
                <div className={'opacity-100'}>{filteredPostsCount}</div>
                <div className={'mx-2 opacity-70'}>/</div>
                <div className={'opacity-70'}>{totalPostsCount}</div>
              </div>
            </div>

            <div id='SEARCH'
              className={'bg-main-lightest my-4 flex items-center overflow-hidden p-6 py-4 text-[16px] shadow-sm lg:m-4 lg:rounded-lg'}>
              <FaMagnifyingGlass className={'text-main/80 mr-3 h-6 w-6'} />
              <input id='SEARCH_INPUT'
                ref={refInputSearch}
                type='text'
                placeholder={i18n.searchPlaceholder}
                className={'grow bg-transparent py-2'}
                value={searchString}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value)}
              />
              <FaXmark className={'text-main ml-3 h-6 w-6 cursor-pointer opacity-20 hover:opacity-100'} onClick={() => setSearchString('')} />
            </div>

          </div>

          <div id='CATEGORIES_WRAPPER'
            className={'flex w-full grow flex-col lg:w-auto'}>

            <div id='CATEGORIES_TITLE'
              className={'text-main-dark/90 drop-shadow-light1px mx-4 my-0 flex items-center justify-between text-[24px] sm:text-[32px] lg:m-4'}>
              {i18n.categories}
              <div className={cn('flex text-[24px]', selectedCategories.length === 0 ? 'opacity-30' : 'opacity-100')}>
                <div className={'opacity-100'}>{selectedCategories.length === 0 ? categories.length : selectedCategories.length}</div>
                <div className={'mx-2 opacity-70'}>/</div>
                <div className={'opacity-70'}>{categories.length}</div>
              </div>
            </div>

            <div id='CATEGORIES_PANEL'
              className={'my-4 grow overflow-hidden shadow-sm lg:m-4 lg:rounded-lg'}>
              <div id='CATEGORIES'
                className={'bg-main-lightest flex max-h-[225px] flex-col gap-4 overflow-y-scroll p-4 py-6 text-[16px]'}>
                {categories.map((category: Category, index: number) => (
                  <div id='CATEGORY'
                    key={index}
                    className={'flex cursor-pointer items-center justify-between px-2 hover:opacity-100 ' + (
                      selectedCategories.length === 0 || selectedCategories.includes(category.id) ? 'opacity-80' : 'opacity-40'
                    )}
                    onClick={() => switchCategory(category.id)}
                  >
                    <div id='CATEGORY_SELECTED' className={'flex items-center w-6 h-6 mr-3 drop-shadow-light1px ' + (
                      selectedCategories.includes(category.id) ? 'text-blue-light' : 'text-black/20'
                    )}>
                      <FaCheck className={'w-full'} />
                    </div>
                    <div id='CATEGORY_NAME' className={'text-main-dark'}>
                      {category.name}
                    </div>
                    <div className={'grow'} />
                    <div id='CATEGORY_NB_POSTS' className={'text-main-dark'}>
                      ({category.posts.length + (category.posts.length == MAX_POSTS_PER_CATEGORY ? '+' : '')})
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>

      <FlexCol id='POSTS_WRAPPER' className='w-full'>

        {(loadingNext || loadingPrev) && posts.length === 0 ? (
          <div id='LOADING' className={'flex items-center justify-center py-16'}>
            <div className={'text-main-lightest flex items-center gap-3 text-lg'}>
              <div className={'h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent'}></div>
              {i18n.loading}
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div id='NO_RESULTS' className={'flex flex-col items-center justify-center px-8 py-16'}>
            <div className={'text-main-lightest text-center'}>
              <h3 className={'mb-4 text-2xl font-medium'}>{i18n.noResults}</h3>
              <p className={'text-main-lightest/70 text-lg'}>{i18n.noResultsDescription}</p>
            </div>
          </div>
        ) : (
          <>
            <div id='POSTS_TITLE'
              className={'text-main-lightest flex w-full items-center justify-between px-4 text-[24px] sm:text-[32px]'}>
              {i18n.newPostsTitle}
              <div className={cn('ml-4 flex text-[24px]', filteredPostsCount === totalPostsCount ? 'opacity-30' : 'opacity-100')}>
                <div className={'opacity-100'}>{filteredPostsCount}</div>
                <div className={'mx-2 opacity-70'}>/</div>
                <div className={'opacity-70'}>{totalPostsCount}</div>
              </div>
            </div>

            {/* SEO & Navigation Footer 
              The Link is always present in the DOM for search engines to crawl next pages (?page=X).
            */}
            <div className="flex w-full justify-center pt-8 pb-4">
              {loadMoreButton(false)}
              </div>

            <div id='POSTS'
              className={'grid grid-cols-1 gap-8 px-0 py-4 sm:px-8 lg:grid-cols-2 lg:p-8'}>
              {posts
                .map((post: Post, index: number) => (
                  <PostCard post={post} key={index} query={searchString} className={''} langCode={langCode} />
                ))}
            </div>
          </>
        )}

      </FlexCol>

      {/* SEO & Navigation Footer 
        The Link is always present in the DOM for search engines to crawl next pages (?page=X).
      */}
      <div ref={loaderRef} className="w-full h-4" />
      <div className="flex w-full justify-center pt-4 pb-8">
        {loadMoreButton(true)}
      </div>
    </FlexCol>
  )
}